import { hex, type Color } from "../../common/color.js";
import { Control } from "./Control.js";
import { makeMouseArgs, type DragEventArgs, type DragPayload } from "./events.js";
import type { Point, Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import type { Desktop } from "./Desktop.js";

/**
 * A ghost that wants to be told whether what is under it would take the drop.
 *
 * `DragGhost` implements this; a caller using a control of its own as the
 * ghost can implement it too and get the same running feedback.
 */
export interface DropFeedback {
  setDropValid(valid: boolean): void;
}

/** True when `control` wants drop-validity updates. */
export function isDropFeedback(control: unknown): control is DropFeedback {
  return typeof (control as DropFeedback | null)?.setDropValid === "function";
}

export interface DragOptions {
  /** Drawn under the pointer for the length of the drag. */
  ghost?: Control | null;
  /**
   * Where the pointer sits inside the ghost, in the ghost's own pixels.
   * Defaults to its center, which is what a picked-up item wants.
   */
  grab?: Point;
  /** Cursor over something that will take the payload. */
  dropCursor?: string;
  /** Cursor over anything that will not. */
  noDropCursor?: string;
}

/**
 * A drag in flight.
 *
 * Deliberately not the DOM's drag-and-drop: there are no DOM widgets on the
 * canvas to drag, the browser's drag images cannot be themed, and its events
 * do not fire for a synthetic pointer, which would put every drag beyond the
 * reach of the test suite. This is the same idea built on the pointer stream
 * the rest of the framework already uses.
 *
 * The session is modal while it runs — the InputManager routes moves and the
 * release to it and to nothing else — so the press that began the drag never
 * completes as a click.
 */
export class DragSession {
  readonly payload: DragPayload;

  #desktop: Desktop;
  #ghost: Control | null;
  #grab: Point;
  #dropCursor: string;
  #noDropCursor: string;
  #target: Control | null = null;
  #done = false;

  constructor(desktop: Desktop, payload: DragPayload, options: DragOptions = {}) {
    this.#desktop = desktop;
    this.payload = payload;
    this.#ghost = options.ghost ?? null;
    this.#grab = options.grab ?? {
      x: (this.#ghost?.width ?? 0) / 2,
      y: (this.#ghost?.height ?? 0) / 2,
    };
    this.#dropCursor = options.dropCursor ?? "copy";
    this.#noDropCursor = options.noDropCursor ?? "no-drop";
  }

  /** The control currently under the pointer that would take the payload. */
  get target(): Control | null {
    return this.#target;
  }

  get ghost(): Control | null {
    return this.#ghost;
  }

  /** False once the drag has been dropped or canceled. */
  get active(): boolean {
    return !this.#done;
  }

  /** Track the pointer: move the ghost, and re-pick the drop target. */
  moveTo(screen: Point): void {
    if (this.#done) return;
    this.#ghost?.setLocation(Math.round(screen.x - this.#grab.x), Math.round(screen.y - this.#grab.y));

    const next = this.#targetAt(screen);
    // Tell the ghost before anything else, so the badge it paints matches the
    // target this move settled on rather than the previous one.
    if (isDropFeedback(this.#ghost)) this.#ghost.setDropValid(next !== null);
    if (next !== this.#target) {
      const previous = this.#target;
      this.#target = next;
      if (previous) previous.emit("dragleave", this.#args(previous, screen));
      if (next) next.emit("dragenter", this.#args(next, screen));
    }
    if (next) next.emit("dragover", this.#args(next, screen));
    this.#desktop.setCursor(next ? this.#dropCursor : this.#noDropCursor);
    this.#desktop.invalidate();
  }

  /**
   * Release over `screen`. Returns whether anything took the payload — a
   * target can still refuse at this point by clearing `accepted`, which is
   * what a full inventory does.
   */
  drop(screen: Point): boolean {
    if (this.#done) return false;
    this.moveTo(screen);
    const target = this.#target;
    let accepted = false;
    if (target) {
      const args = this.#args(target, screen);
      target.handleDrop(args);
      accepted = args.accepted;
    }
    this.#finish(accepted);
    return accepted;
  }

  /** End the drag with nothing taking the payload. */
  cancel(): void {
    if (this.#done) return;
    const target = this.#target;
    this.#target = null;
    if (target) {
      target.emit("dragleave", this.#args(target, this.#desktop.input.pointerPosition));
    }
    this.#finish(false);
  }

  #finish(accepted: boolean): void {
    this.#done = true;
    this.#ghost = null;
    this.#target = null;
    this.#desktop.endDrag(this);
    this.#desktop.setCursor("default");
    this.payload.source?.emit("dragend", { payload: this.payload, accepted });
    this.#desktop.invalidate();
  }

  /**
   * The innermost control under `p` that will take the payload, or the first
   * ancestor that will. Walking up matters: an ItemSlot may decline a stack
   * it has no room for while the InventoryGrid behind it takes it happily.
   */
  #targetAt(p: Point): Control | null {
    let control = this.#desktop.hitTestScreen(p);
    while (control) {
      if (
        control.effectivelyEnabled &&
        this.#desktop.allowsInput(control) &&
        control.canAcceptDrop(this.payload, control.fromScreen(p))
      ) {
        return control;
      }
      control = control.parent;
    }
    return null;
  }

  #args(target: Control, screen: Point): DragEventArgs {
    const local = target.fromScreen(screen);
    return {
      ...makeMouseArgs(local.x, local.y, screen, {}, 0),
      payload: this.payload,
      accepted: true,
    };
  }
}

export interface DragGhostOptions {
  width: number;
  height: number;
  paint: (rc: RenderContext, theme: Theme, r: Rect) => void;
  /**
   * Badge the corner with a tick or a cross as the pointer moves over things
   * that will and will not take the drop. On by default.
   */
  showDropBadge?: boolean;
  /** Badge diameter in px, before it is clamped to fit the ghost. */
  badgeSize?: number;
}

/** Fixed rather than themed: a "yes" has to read as yes in every theme. */
const BADGE_VALID = hex("#43a85c");
const BADGE_INVALID = hex("#d44a4a");
const BADGE_MARK = hex("#ffffff");
const DEFAULT_BADGE_SIZE = 18;

/**
 * The thing that follows the pointer during a drag.
 *
 * Any control can serve as a ghost; this is the convenient one, painting
 * whatever it is handed. Like a tooltip it answers no hit test, so the
 * session can see what is underneath it.
 */
export class DragGhost extends Control implements DropFeedback {
  #paint: (rc: RenderContext, theme: Theme, r: Rect) => void;
  /** Null until the session has picked a target for the first time. */
  #dropValid: boolean | null = null;

  showDropBadge: boolean;
  badgeSize: number;

  constructor(options: DragGhostOptions) {
    super({ width: options.width, height: options.height, tabStop: false });
    this.#paint = options.paint;
    this.showDropBadge = options.showDropBadge ?? true;
    this.badgeSize = options.badgeSize ?? DEFAULT_BADGE_SIZE;
  }

  /**
   * Whether the thing under the pointer would take the drop: true, false, or
   * null before the session has looked.
   */
  get dropValid(): boolean | null {
    return this.#dropValid;
  }

  /** Called by the session on every move. */
  setDropValid(valid: boolean): void {
    if (this.#dropValid === valid) return;
    this.#dropValid = valid;
    this.invalidate();
  }

  protected override hitTestSelf(): boolean {
    return false;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    this.#paint(rc, theme, this.localRect);
    if (!this.showDropBadge || this.#dropValid === null) return;
    drawDropBadge(rc, this.localRect, this.badgeSize, this.#dropValid);
  }
}

/**
 * A tick or a cross in the ghost's bottom-right corner.
 *
 * The cursor already says the same thing, but only on a machine that has one:
 * a canvas HUD driven by touch or a pad has no cursor to read, and even with a
 * mouse the pointer is the one part of the screen the eye is not looking at
 * while it tracks what is being carried. The badge rides on the thing you are
 * actually watching.
 *
 * Drawn from primitives rather than the glyph registry so that `core` keeps
 * no value-dependency on `themes`, and kept fully inside the ghost's own
 * rectangle so nothing relies on painting past its bounds.
 */
export function drawDropBadge(rc: RenderContext, r: Rect, size: number, valid: boolean): void {
  const side = Math.max(10, Math.min(size, Math.min(r.width, r.height) * 0.6));
  const radius = side / 2;
  const cx = r.x + r.width - radius - 1;
  const cy = r.y + r.height - radius - 1;
  const center: Point = { x: cx, y: cy };

  rc.fillArc(center, 0, radius, 0, Math.PI * 2, valid ? BADGE_VALID : BADGE_INVALID);
  rc.fillArc(center, radius - 1, radius, 0, Math.PI * 2, withBadgeEdge(valid));

  const stroke = Math.max(1.5, side * 0.14);
  const ink: Color = BADGE_MARK;
  if (valid) {
    // A tick: down to the low point, then up and out past it.
    rc.drawLine(
      { x: cx - radius * 0.42, y: cy - radius * 0.04 },
      { x: cx - radius * 0.1, y: cy + radius * 0.34 },
      stroke,
      ink,
    );
    rc.drawLine(
      { x: cx - radius * 0.1, y: cy + radius * 0.34 },
      { x: cx + radius * 0.46, y: cy - radius * 0.36 },
      stroke,
      ink,
    );
    return;
  }
  const arm = radius * 0.36;
  rc.drawLine({ x: cx - arm, y: cy - arm }, { x: cx + arm, y: cy + arm }, stroke, ink);
  rc.drawLine({ x: cx + arm, y: cy - arm }, { x: cx - arm, y: cy + arm }, stroke, ink);
}

/** A darker rim, so the badge reads against a light ghost as well as a dark one. */
function withBadgeEdge(valid: boolean): Color {
  const c = valid ? BADGE_VALID : BADGE_INVALID;
  return { r: c.r * 0.6, g: c.g * 0.6, b: c.b * 0.6, a: 1 };
}
